import { useEffect, useRef } from 'react';
import { getEffectiveExpMs, triggerSessionExpired } from '../logic/sessionExpiry';

/**
 * Always-mounted (route-independent) session-expiry watcher. Rendered
 * unconditionally from HomeComponent regardless of route, so it stays active
 * on the real-exam player route where Header (and its old countdown) isn't
 * mounted. Reports remaining time via onTick so HomeComponent can pass it
 * down to Header for the visible countdown display.
 *
 * The pre-flight gate (ensureSessionTimeForTestStart) is the primary defense
 * against starting a test the session can't outlast — this watcher is a
 * defense-in-depth safety net for what the gate can't cover (device
 * sleep/suspend, clock jump, unusually long sessions).
 */
function SessionWatcher({ onTick }) {
  const activeRef = useRef(true);
  const intervalIdRef = useRef(null);

  useEffect(() => {
    activeRef.current = true;

    const check = () => {
      if (!activeRef.current) return;
      const expMs = getEffectiveExpMs();
      const remainingMs = expMs != null ? expMs - Date.now() : null;
      if (onTick) onTick(remainingMs);
      if (remainingMs != null && remainingMs <= 0) {
        activeRef.current = false;
        clearInterval(intervalIdRef.current);
        triggerSessionExpired('expired');
      }
    };

    // Assign the interval id before the first check runs, so the very first
    // tick (which may detect an already-expired session) can safely clear it.
    intervalIdRef.current = setInterval(check, 1000);
    check();

    return () => {
      activeRef.current = false;
      clearInterval(intervalIdRef.current);
    };
  }, [onTick]);

  return null;
}

export default SessionWatcher;
